import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Icon } from '@statsbomb/kitbag-components';
import { useDistinctTeamColors } from '../../utils/hooks/useDistinctTeamColors';
import { PlayByPlayHeaderContainer } from '../../pages/Game/GamePlayByPlay/PlayByPlay.styles';

const TeamsLineWrap = styled.div`
  ${PlayByPlayHeaderContainer} {
    padding: 0;
    justify-content: center;
  }
`;
TeamsLineWrap.displayName = 'TeamsLineWrap';

// "vs" / "@" use the weaker ink so the team names and score lead.
const MutedText = styled.span`
  color: ${({ theme }) => theme.colours.ink.primary.weak};
`;
MutedText.displayName = 'MutedText';

const formatShortDate = (dateString) => {
  if (!dateString) return null;
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  const date = parts
    ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
    : new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-US', { day: 'numeric', month: 'numeric' });
};

const toColors = (team) => ({
  primary: team.colorPrimary,
  secondary: team.colorSecondary,
  tertiary: team.colorTertiary,
});

function GameTeamsLine({ game, selectedTeamId }) {
  const isHomeTeam = game.homeTeam.id === selectedTeamId;
  const selectedTeam = isHomeTeam ? game.homeTeam : game.awayTeam;
  const opponentTeam = isHomeTeam ? game.awayTeam : game.homeTeam;
  const selectedScore = isHomeTeam ? game.homeScore : game.awayScore;
  const opponentScore = isHomeTeam ? game.awayScore : game.homeScore;
  const validScores =
    typeof selectedScore === 'number' && typeof opponentScore === 'number';

  const { selectedTeamColor, opponentTeamColor } = useDistinctTeamColors(
    toColors(selectedTeam),
    toColors(opponentTeam),
  );

  const dateLabel = [game.roundShortName, formatShortDate(game.date)]
    .filter(Boolean)
    .join(' ');

  return (
    <TeamsLineWrap>
      <PlayByPlayHeaderContainer>
        <span data-testid="game-header-date">{dateLabel}</span>
        <span style={{ color: selectedTeamColor }}>
          <Icon variant="HalfMoonLeft" />
        </span>
        <span>{selectedTeam.name}</span>
        <MutedText>{isHomeTeam ? 'vs' : '@'}</MutedText>
        <span style={{ color: opponentTeamColor }}>
          <Icon variant="HalfMoonTop" />
        </span>
        <span>{opponentTeam.name}</span>
        {validScores && `(${selectedScore} - ${opponentScore})`}
      </PlayByPlayHeaderContainer>
    </TeamsLineWrap>
  );
}

GameTeamsLine.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  game: PropTypes.object.isRequired,
  selectedTeamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default GameTeamsLine;
